import React, { SyntheticEvent } from "react";

import { Dialog, IconName, MaybeElement } from "@blueprintjs/core";
import { IViewProps, View } from "core/decorators/View";
import styled, { createGlobalStyle, css } from "styled-components";
import { toBlockCss } from "../common/BlockStylesConfig";
import { DialogConfig } from "./DialogConfig";


export enum DialogVariant {
	DEFAULT = "default",
	MODEL = "model"
}

export interface IDialogViewProps extends IViewProps<DialogConfig> {
	title?: string | JSX.Element;
	icon?: IconName | MaybeElement;
	autoFocus?: boolean;
	canEscapeKeyClose?: boolean;
	canOutsideClickClose?: boolean;
	enforceFocus?: boolean;
	isOpen?: boolean;
	onClosed?: (node: HTMLElement) => void;
	onClose?: (event?: SyntheticEvent<HTMLElement>) => void;
	usePortal?: boolean;
	isCloseButtonShown?: boolean;
}


interface IStyledDialogProps {
	config: DialogConfig;
	children: React.ReactNode;
	isOpen: boolean;
}

const styles = ({config, isOpen}) => css`
	margin: 0;
	width: ${config.width} !important;
	height: ${config.height} !important;
	min-height: ${config.minHeight} !important;

	${(config.fullscreen && isOpen && css`
		display: block;
		position: fixed;
		top: 0;
		left: 0;
		right; 0;
	`) || (config.fullscreen && css`
		display: none;
	`)}

	${toBlockCss(config.styles)}

	& .bp3-dialog-header {
		${toBlockCss(config.headerStyles)}
	}

	& .bp3-dialog-header .bp3-heading {
		color: ${config.headerStyles.color};
		background-color:${config.headerStyles.backgroundColor};
		padding:${config.headerStyles.padding};
	}
`;

const fullScreenOuter = ({config, isOpen}) => css`
	${(isOpen && css`
		display: block;
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right; 0;
		height: 100vh;
		width: 100vw;
	`) || (css`
		display: none;
	`)}

	z-index: 8300;
	overflow-y: auto;
	background-color: ${config.styles.backgroundColor}
`;

const fullScreenInner = ({config}) => css`
	${toBlockCss(config.styles)}
`;

const FullScreenGlobalStyle = createGlobalStyle<{isOpen: boolean}>(({isOpen}) => css`
	body {
		${isOpen && `overflow: hidden`};
	}
`);

const StyledDialog = styled(Dialog)<IStyledDialogProps>(styles);
const FullScreenDialog = styled('div')<IStyledDialogProps>(fullScreenOuter);
const FullScreenDialogInner = styled('div')<IStyledDialogProps>(fullScreenInner);

@View({id: "dialog", configClass: DialogConfig})
export class DialogView extends React.PureComponent<IDialogViewProps> {

	public render() {
		const config = this.props.config;
		const { icon, title, autoFocus, canEscapeKeyClose, canOutsideClickClose, enforceFocus, usePortal, fullscreen } = this.props.config;

		return fullscreen ? (
			<FullScreenDialog
				isOpen={this.props.isOpen}
				config={config}
			>
				<FullScreenGlobalStyle isOpen={this.props.isOpen} />
				<FullScreenDialogInner
					isOpen={this.props.isOpen}
					config={config}
				>
					{(this.props.isOpen && this.props.children) || null}
				</FullScreenDialogInner>
			</FullScreenDialog>
		) : (
			<StyledDialog
				icon={icon}
				onClose={this.props.onClose}
				isOpen={this.props.isOpen}
				onClosed={this.props.onClosed}
				title={title || this.props.title}
				autoFocus={autoFocus}
				canEscapeKeyClose={canEscapeKeyClose}
				canOutsideClickClose={canOutsideClickClose}
				enforceFocus={enforceFocus}
				usePortal={usePortal}
				isCloseButtonShown={config.isCloseButtonShown}
				config={config}
			>
				{this.props.children}
			</StyledDialog>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/dialog/Dialog.view.tsx